import React, { useState, useEffect } from 'react';

const DateRangeFilter = ({ onDateRangeChange, defaultPreset = 'all', currentDateRange = null }) => {
  // Helper function to format date for input
  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Determine initial preset based on currentDateRange if provided
  const determinePresetFromRange = (range) => {
    if (!range || (!range.startDate && !range.endDate)) {
      return 'all';
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayEnd = new Date(today);
    todayEnd.setHours(23, 59, 59, 999);
    
    if (range.startDate && range.endDate) {
      const startDate = new Date(range.startDate);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(range.endDate);
      endDate.setHours(23, 59, 59, 999);
      
      // Check if it matches "today"
      if (startDate.getTime() === today.getTime() && endDate.getTime() === todayEnd.getTime()) {
        return 'today';
      }
      
      // Check if it matches "thisWeek"
      const dayOfWeek = today.getDay();
      const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      const weekStart = new Date(today);
      weekStart.setDate(diff);
      weekStart.setHours(0, 0, 0, 0);
      if (startDate.getTime() === weekStart.getTime() && endDate.getTime() === todayEnd.getTime()) {
        return 'thisWeek';
      }
      
      // Check if it matches "thisMonth"
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      if (startDate.getTime() === monthStart.getTime() && endDate.getTime() === todayEnd.getTime()) {
        return 'thisMonth';
      }
      
      // Check if it matches "thisYear"
      const yearStart = new Date(today.getFullYear(), 0, 1);
      if (startDate.getTime() === yearStart.getTime() && endDate.getTime() === todayEnd.getTime()) {
        return 'thisYear';
      }
      
      // If it doesn't match any preset, it's custom
      return 'custom';
    }
    
    return 'all';
  };
  
  const initialPreset = currentDateRange ? determinePresetFromRange(currentDateRange) : (defaultPreset || 'all');
  const [preset, setPreset] = useState(initialPreset);
  const [customStartDate, setCustomStartDate] = useState(
    currentDateRange?.startDate ? formatDateForInput(currentDateRange.startDate) : ''
  );
  const [customEndDate, setCustomEndDate] = useState(
    currentDateRange?.endDate ? formatDateForInput(currentDateRange.endDate) : ''
  );
  const [showCustomPicker, setShowCustomPicker] = useState(initialPreset === 'custom');
  const [hasUserSelectedPreset, setHasUserSelectedPreset] = useState(!!currentDateRange);
  
  const getDateRange = (presetValue) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let startDate = null;
    let endDate = new Date();
    endDate.setHours(23, 59, 59, 999);

    switch (presetValue) {
      case 'today':
        startDate = new Date(today);
        break;
      case 'thisWeek':
        const dayOfWeek = today.getDay();
        const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Monday
        startDate = new Date(today.setDate(diff));
        today.setHours(0, 0, 0, 0);
        break;
      case 'thisMonth':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'thisYear':
        startDate = new Date(today.getFullYear(), 0, 1);
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59, 999);
        } else {
          return null;
        }
        break;
      case 'all':
      default:
        return null; // No date filter
    }

    return { startDate, endDate };
  };
  
  // Reset preset only when defaultPreset changes and user hasn't manually selected a preset
  // This prevents resetting preset when defaultPreset changes from undefined to 'all' after user selection
  useEffect(() => {
    if (defaultPreset !== undefined && !hasUserSelectedPreset && preset !== defaultPreset) {
      setPreset(defaultPreset);
      setCustomStartDate('');
      setCustomEndDate('');
      setShowCustomPicker(false);
      // For 'all' preset, pass null to clear date filter
      if (defaultPreset === 'all') {
        onDateRangeChange(null);
      } else if (defaultPreset) {
        const range = getDateRange(defaultPreset);
        onDateRangeChange(range);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultPreset]);

  const handlePresetChange = (newPreset) => {
    setPreset(newPreset);
    setHasUserSelectedPreset(true); // Mark that user has manually selected a preset
    setShowCustomPicker(newPreset === 'custom');
    
    if (newPreset !== 'custom') {
      // Clear custom dates when switching to a preset
      setCustomStartDate('');
      setCustomEndDate('');
      const range = getDateRange(newPreset);
      onDateRangeChange(range);
    } else {
      // When switching to custom, clear any existing filter until dates are selected
      setCustomStartDate('');
      setCustomEndDate('');
      onDateRangeChange(null);
    }
  };

  const handleCustomDateChange = () => {
    if (customStartDate && customEndDate) {
      // Create dates in local timezone to avoid timezone issues
      const startDateStr = customStartDate; // Format: YYYY-MM-DD
      const endDateStr = customEndDate; // Format: YYYY-MM-DD
      
      // Parse dates as local dates (not UTC)
      const [startYear, startMonth, startDay] = startDateStr.split('-').map(Number);
      const [endYear, endMonth, endDay] = endDateStr.split('-').map(Number);
      
      const startDate = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0);
      const endDate = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);
      
      // Validate that end date is not before start date
      if (endDate < startDate) {
        onDateRangeChange(null); // Clear filter if invalid
        return;
      }
      
      onDateRangeChange({ startDate, endDate });
    } else {
      // If dates are not complete, clear the filter
      onDateRangeChange(null);
    }
  };

  // Update preset when currentDateRange changes (e.g., when switching tabs)
  useEffect(() => {
    if (currentDateRange) {
      const detectedPreset = determinePresetFromRange(currentDateRange);
      if (detectedPreset !== preset) {
        setPreset(detectedPreset);
        setShowCustomPicker(detectedPreset === 'custom');
        if (detectedPreset === 'custom') {
          setCustomStartDate(currentDateRange.startDate ? formatDateForInput(currentDateRange.startDate) : '');
          setCustomEndDate(currentDateRange.endDate ? formatDateForInput(currentDateRange.endDate) : '');
        } else {
          setCustomStartDate('');
          setCustomEndDate('');
        }
        setHasUserSelectedPreset(true);
      }
    } else if (!currentDateRange && preset !== 'all') {
      // If dateRange is cleared, reset to 'all'
      setPreset('all');
      setCustomStartDate('');
      setCustomEndDate('');
      setShowCustomPicker(false);
      setHasUserSelectedPreset(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDateRange]);

  return (
    <div style={{ 
      display: 'flex', 
      gap: '0.5rem', 
      alignItems: 'center',
      flexWrap: 'wrap',
      marginBottom: '1rem',
      padding: '0'
    }}>
      <label style={{ 
        fontSize: '0.875rem', 
        fontWeight: '500', 
        color: '#374151',
        marginRight: '0.5rem'
      }}>
        Фільтр за датою:
      </label>
      
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          onClick={() => handlePresetChange('all')}
          style={{
            padding: '0.5rem 1rem',
            background: preset === 'all' ? '#3b82f6' : 'white',
            color: preset === 'all' ? 'white' : '#374151',
            border: '1px solid #e5e7eb',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: preset === 'all' ? '600' : '400'
          }}
        >
          Всі
        </button>
        
        <button
          onClick={() => handlePresetChange('today')}
          style={{
            padding: '0.5rem 1rem',
            background: preset === 'today' ? '#3b82f6' : 'white',
            color: preset === 'today' ? 'white' : '#374151',
            border: '1px solid #e5e7eb',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: preset === 'today' ? '600' : '400'
          }}
        >
          Сьогодні
        </button>
        
        <button
          onClick={() => handlePresetChange('thisWeek')}
          style={{
            padding: '0.5rem 1rem',
            background: preset === 'thisWeek' ? '#3b82f6' : 'white',
            color: preset === 'thisWeek' ? 'white' : '#374151',
            border: '1px solid #e5e7eb',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: preset === 'thisWeek' ? '600' : '400'
          }}
        >
          Цей тиждень
        </button>
        
        <button
          onClick={() => handlePresetChange('thisMonth')}
          style={{
            padding: '0.5rem 1rem',
            background: preset === 'thisMonth' ? '#3b82f6' : 'white',
            color: preset === 'thisMonth' ? 'white' : '#374151',
            border: '1px solid #e5e7eb',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: preset === 'thisMonth' ? '600' : '400'
          }}
        >
          Цей місяць
        </button>
        
        <button
          onClick={() => handlePresetChange('thisYear')}
          style={{
            padding: '0.5rem 1rem',
            background: preset === 'thisYear' ? '#3b82f6' : 'white',
            color: preset === 'thisYear' ? 'white' : '#374151',
            border: '1px solid #e5e7eb',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: preset === 'thisYear' ? '600' : '400'
          }}
        >
          Цей рік
        </button>
        
        <button
          onClick={() => handlePresetChange('custom')}
          style={{
            padding: '0.5rem 1rem',
            background: preset === 'custom' ? '#3b82f6' : 'white',
            color: preset === 'custom' ? 'white' : '#374151',
            border: '1px solid #e5e7eb',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: preset === 'custom' ? '600' : '400'
          }}
        >
          Власний діапазон
        </button>
      </div>

      {showCustomPicker && (
        <div style={{ 
          display: 'flex', 
          gap: '0.5rem', 
          alignItems: 'center',
          padding: '0.5rem',
          background: '#f9fafb',
          borderRadius: '4px',
          border: '1px solid #e5e7eb'
        }}>
          <label style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            Від:
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => {
                const newStartDate = e.target.value;
                setCustomStartDate(newStartDate);
                
                // Use the new value from event, not state (state updates are async)
                if (newStartDate && customEndDate) {
                  // If end date is before new start date, clear it
                  if (new Date(customEndDate) < new Date(newStartDate)) {
                    setCustomEndDate('');
                    onDateRangeChange(null);
                  } else {
                    // Both dates are set, update the filter immediately
                    const [startYear, startMonth, startDay] = newStartDate.split('-').map(Number);
                    const [endYear, endMonth, endDay] = customEndDate.split('-').map(Number);
                    const startDate = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0);
                    const endDate = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);
                    onDateRangeChange({ startDate, endDate });
                  }
                } else {
                  // Only start date is set, clear filter
                  onDateRangeChange(null);
                }
              }}
              style={{
                marginLeft: '0.5rem',
                padding: '0.25rem 0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '0.875rem'
              }}
            />
          </label>
          
          <label style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            До:
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => {
                const newEndDate = e.target.value;
                setCustomEndDate(newEndDate);
                
                // Use the new value from event, not state
                if (newEndDate && customStartDate) {
                  // Validate that end date is not before start date
                  if (new Date(newEndDate) >= new Date(customStartDate)) {
                    // Both dates are set, trigger update immediately
                    const [startYear, startMonth, startDay] = customStartDate.split('-').map(Number);
                    const [endYear, endMonth, endDay] = newEndDate.split('-').map(Number);
                    const startDate = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0);
                    const endDate = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);
                    onDateRangeChange({ startDate, endDate });
                  } else {
                    // End date is before start date, don't apply filter
                    onDateRangeChange(null);
                  }
                } else if (!newEndDate) {
                  // End date cleared, clear filter
                  onDateRangeChange(null);
                }
              }}
              min={customStartDate || undefined}
              style={{
                marginLeft: '0.5rem',
                padding: '0.25rem 0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '0.875rem'
              }}
            />
          </label>
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;

